/**
 * Admin → Learner reports. Find anyone who finished a test, open their saved
 * report and print it. Missing or partial copies are completed from the
 * learner's answers and saved. Every open is written to report_access_log,
 * because many of these reports belong to children.
 */
import CollegePlanSection from "@/components/report/CollegePlanSection";
import CounsellorRecommendations from "@/components/report/CounsellorRecommendations";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Printer, ArrowLeft, Search, Save } from "lucide-react";
import DnaSurface from "@/components/report/DnaSurface";
import LivingReport from "@/components/report/LivingReport";
import type { StudentPayload } from "@/components/report/reportTypes";
import ReportHistory from "@/components/report/ReportHistory";
import SavedReportRoadmaps from "@/components/report/SavedReportRoadmaps";
import { snapshotReport } from "@/lib/reportSnapshots";
import { loadLatestCompletedRun } from "@/features/assessment-v2/persistedSnapshot";
import {
  buildDerivedStudentPayload, mergeStudentPayloads, sealedPayloadIsThin,
} from "@/features/assessment-v2/derivedPayload";

interface Row {
  learner_id: string;
  email: string | null;
  full_name: string | null;
  completed_at: string;
  saved: "full" | "partial" | "none";
}

async function latestReport(learnerId: string) {
  const { data } = await supabase
    .from("learner_reports")
    .select("id, payload, generated_at")
    .eq("learner_id", learnerId).eq("audience", "student")
    .order("generated_at", { ascending: false }).limit(1).maybeSingle();
  return data;
}

/** Open (and if needed complete + save) one learner's report. */
async function openReport(learnerId: string, name: string | null) {
  const [row, run] = await Promise.all([latestReport(learnerId), loadLatestCompletedRun({ learnerId })]);
  let sealed = (row?.payload ?? null) as unknown as StudentPayload | null;
  if (run && sealed?.saved_copy?.run_id && sealed.saved_copy.run_id !== run.sessionId) sealed = null;
  let reportId = row?.id ?? null;
  if (sealedPayloadIsThin(sealed)) {
    const derived = await buildDerivedStudentPayload({ learnerId, displayName: name, state: run?.state, allowLocal: false });
    if (derived) {
      sealed = mergeStudentPayloads(sealed, derived);
    }
  }
  if (sealed && !sealedPayloadIsThin(sealed)) {
    const { data, error } = await supabase.functions.invoke("report-save", {
      body: { learner_id: learnerId, payload: snapshotReport(sealed, run), ...(run ? { run_id: run.sessionId } : {}) },
    });
    // Saving must not prevent opening the report already available.
    if (error || (!data?.saved && data?.reason !== "exists")) return { payload: sealed, reportId };
    if (data?.report_id) reportId = data.report_id;
    if (data?.payload) sealed = data.payload;
  }
  return { payload: sealed, reportId };
}

export default function AdminReports() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<{ row: Row; payload: StudentPayload | null } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [bulk, setBulk] = useState<string | null>(null);

  const load = async () => {
    const { data: runs } = await supabase
      .from("runtime_sessions")
      .select("learner_id, completed_at, metadata")
      .eq("status", "completed")
      .order("completed_at", { ascending: false })
      .limit(1000);
    const latest = new Map<string, string>();
    for (const r of runs ?? []) {
      const m = (r.metadata ?? {}) as Record<string, unknown>;
      if (m.source !== "assessment-v2" || m.seeded_by || !r.completed_at) continue;
      if (!latest.has(r.learner_id)) latest.set(r.learner_id, r.completed_at);
    }
    const ids = [...latest.keys()];
    if (!ids.length) { setRows([]); return; }
    const [{ data: profs }, { data: reps }] = await Promise.all([
      supabase.rpc("admin_list_users", { _limit: 1000 }),
      supabase.from("learner_reports").select("learner_id, payload, generated_at")
        .in("learner_id", ids).eq("audience", "student").order("generated_at", { ascending: false }),
    ]);
    const status = new Map<string, Row["saved"]>();
    for (const r of reps ?? []) {
      if (status.get(r.learner_id) === "full") continue;
      status.set(r.learner_id, sealedPayloadIsThin(r.payload as unknown as StudentPayload) ? "partial" : "full");
    }
    const pm = new Map(((profs ?? []) as { user_id: string; email: string | null; full_name: string | null }[])
      .map((p) => [p.user_id, p]));
    setRows(ids.map((id) => ({
      learner_id: id,
      email: pm.get(id)?.email ?? null,
      full_name: pm.get(id)?.full_name ?? null,
      completed_at: latest.get(id) ?? "",
      saved: status.get(id) ?? "none",
    })));
  };

  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (rows ?? []).filter((r) => !t || `${r.email ?? ""} ${r.full_name ?? ""}`.toLowerCase().includes(t));
  }, [rows, q]);

  const view = async (row: Row) => {
    setBusy(row.learner_id);
    try {
      const { payload, reportId } = await openReport(row.learner_id, row.full_name);
      if (user?.id) {
        await supabase.from("report_access_log").insert({
          admin_id: user.id, learner_id: row.learner_id, report_id: reportId, action: "view",
        });
      }
      setOpen({ row, payload });
    } finally { setBusy(null); }
  };

  const saveAll = async () => {
    const todo = (rows ?? []).filter((r) => r.saved !== "full");
    let done = 0;
    for (const r of todo) {
      setBulk(`Saving ${done + 1} of ${todo.length}…`);
      try { await openReport(r.learner_id, r.full_name); } catch { /* keep going */ }
      done++;
    }
    setBulk(null);
    await load();
  };

  if (open) {
    return (
      <div className="mx-auto max-w-5xl p-4 sm:p-8">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <Button variant="ghost" className="h-12 rounded-2xl" onClick={() => setOpen(null)}>
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" /> All reports
          </Button>
          <p className="text-sm text-muted-foreground">{open.row.full_name ?? "Learner"} · {open.row.email}</p>
          <Button variant="outline" className="h-12 rounded-2xl" onClick={() => {
            if (user?.id) void supabase.from("report_access_log").insert({ admin_id: user.id, learner_id: open.row.learner_id, action: "print" });
            window.print();
          }}>
            <Printer className="mr-2 h-4 w-4" aria-hidden="true" /> Print / Save as PDF
          </Button>
        </div>
        {open.payload ? (
          <>
            <ReportHistory learnerId={open.row.learner_id} refresh={0} onSelect={(payload, reportId) => {
              if (user?.id) void supabase.from("report_access_log").insert({ admin_id: user.id,
                learner_id: open.row.learner_id, report_id: reportId, action: "view" });
              setOpen({ ...open, payload });
            }} />
            <DnaSurface payload={open.payload} />
            <SavedReportRoadmaps payload={open.payload} />
            <div className="mt-10 border-t border-border/50 pt-2"><LivingReport payload={open.payload} /></div>
            <CollegePlanSection learnerId={open.row.learner_id} editable snapshot={open.payload.presentation_snapshot?.college_plan} before={open.payload.saved_copy?.saved_at} />
            <CounsellorRecommendations learnerId={open.row.learner_id} editable adminId={user?.id} />
          </>
        ) : (
          <p className="text-muted-foreground">This learner&apos;s answers aren&apos;t enough to build a report yet.</p>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin" className="text-sm text-muted-foreground hover:underline">← Admin Hub</Link>
          <h1 className="mt-2 font-display text-3xl font-bold">Learner reports</h1>
          <p className="text-sm text-muted-foreground">Everyone who finished a test. Each time you open a report, it&apos;s recorded.</p>
        </div>
        <Button className="h-12 rounded-2xl" disabled={!!bulk || !rows?.some((r) => r.saved !== "full")} onClick={saveAll}>
          {bulk ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" aria-hidden="true" />}
          {bulk ?? "Save full copies for all"}
        </Button>
      </div>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input aria-label="Search by email or name" placeholder="Search by email or name" className="h-12 rounded-2xl pl-11"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {!rows ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <Card key={r.learner_id} className="rounded-3xl">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="truncate font-medium">{r.full_name ?? "—"}</p>
                  <p className="truncate text-sm text-muted-foreground">{r.email ?? r.learner_id}</p>
                  <p className="text-xs text-muted-foreground">
                    Finished {new Date(r.completed_at).toLocaleDateString()} ·{" "}
                    {r.saved === "full" ? "Full copy saved" : r.saved === "partial" ? "Partial copy — completes when opened" : "No copy yet — saves when opened"}
                  </p>
                </div>
                <Button variant="outline" className="h-12 rounded-2xl" disabled={busy === r.learner_id} onClick={() => view(r)}>
                  {busy === r.learner_id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Open report
                </Button>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && <p className="text-sm text-muted-foreground">No matches.</p>}
        </div>
      )}
    </div>
  );
}
